/**
 * One place for "something failed and the person should know" (Daniel
 * 2026-10-14: "could we show errors for most things so we don't have to guess
 * as much?").
 *
 * Every failed save or button click used to fail quietly unless that one
 * screen remembered to show its own message. This catches them centrally:
 * main.tsx hands every query and mutation failure to reportFailure(), which
 * decides whether it is worth a banner, writes it in plain words, and keeps
 * the technical details behind a "Copy details" button.
 *
 * What gets a banner, on purpose:
 *   saves and buttons (mutations)  network failure, 403, 429, any 5xx
 *   page loads (queries)           network failure or 5xx, and only when there
 *                                  is nothing on screen to fall back on
 * What does not:
 *   401   the routing layer already sends the person to sign in
 *   400, 404, 409, 422 on a mutation: the screen is expected to say what was
 *   wrong with the input, and a banner on top would just be noise
 *   background refreshes that fail while good data is already showing
 */
import { useSyncExternalStore } from 'react';
import { ApiError } from '../auth/api';

export interface FailureNotice {
  id: number;
  title: string;
  message: string;
  ref?: string;
  /** Pre-formatted text for the Copy details button. */
  details: string;
  count: number;
  at: number;
}

type Kind = 'save' | 'load';

let notices: FailureNotice[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const MAX_SHOWN = 3;
const DEDUPE_MS = 15_000;

function emit() {
  for (const l of listeners) l();
}

function plainMessage(err: ApiError | null, raw: string, kind: Kind): string {
  if (err?.status === 0) return "We couldn't reach the server. Check your internet connection and try again.";
  if (err?.status === 403) {
    const why = raw && raw !== 'HTTP 403' ? ` ${raw}` : '';
    return `Your role is not allowed to do that.${why}`;
  }
  if (err?.status === 429) return 'Too many tries in a row. Wait a minute and try again.';
  if (err && err.status >= 500) {
    return kind === 'save'
      ? 'Something went wrong on our side. Your change was not saved.'
      : 'Something went wrong on our side, so this page could not load its data.';
  }
  return raw || 'Something went wrong.';
}

function shouldShow(err: unknown, kind: Kind, hadData: boolean): boolean {
  if (!(err instanceof ApiError)) return kind === 'save';
  if (err.status === 401) return false;
  if (kind === 'save') {
    return err.status === 0 || err.status === 403 || err.status === 429 || err.status >= 500;
  }
  return (err.status === 0 || err.status >= 500) && !hadData;
}

export function reportFailure(err: unknown, opts: { kind: Kind; hadData?: boolean }): void {
  if (!shouldShow(err, opts.kind, opts.hadData ?? false)) return;
  const api = err instanceof ApiError ? err : null;
  const raw = err instanceof Error ? err.message : String(err);
  const message = plainMessage(api, raw, opts.kind);
  const title = opts.kind === 'save' ? 'That did not save' : 'This page did not load';
  const page = typeof window !== 'undefined' ? window.location.pathname : '';
  const what = api?.path ? `${api.method ?? 'GET'} ${api.path}` : '(not an API call)';
  const now = Date.now();

  // Same failure again within a few seconds: bump the count, do not stack.
  const dup = notices.find((n) => n.details.includes(`What: ${what}\n`) && n.message === message && now - n.at < DEDUPE_MS);
  if (dup) {
    notices = notices.map((n) => (n === dup ? { ...n, count: n.count + 1, at: now } : n));
    emit();
    return;
  }

  const details = [
    'Transaction Rocket error',
    `When: ${new Date(now).toString()}`,
    `Page: ${page}`,
    `What: ${what}`,
    `Status: ${api ? api.status : 'n/a'}`,
    `Message: ${raw}`,
    api?.ref ? `Ref: ${api.ref}` : 'Ref: none',
    '',
  ].join('\n');

  notices = [
    { id: nextId++, title, message, ref: api?.ref, details, count: 1, at: now },
    ...notices,
  ].slice(0, MAX_SHOWN);
  emit();
}

export function dismissFailure(id: number): void {
  notices = notices.filter((n) => n.id !== id);
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function useFailureNotices(): FailureNotice[] {
  return useSyncExternalStore(subscribe, () => notices, () => notices);
}
